// A05c — Admin reset-password via 6-digit code (companion to A05 / A05b).
// URL: /reset-with-code
//
// User reaches this page after submitting /forgot-password. The identifier
// they typed is forwarded via React Router `location.state.identifier` so
// they don't retype it — but the field is still editable in case they want
// to switch identifiers without going back. Body sent to the API:
//   POST /v1/auth/reset-password
//   { identifier, otp_code, new_password }
//
// On 200 → navigate to /login with a success toast. On 4xx error →
// surface the message (admin-recovery-locked / otp_invalid / etc.).
//
// Style: matches SignIn + ForgotPassword (login-bg.png background, Paper
// card, primary admin colour) so the three screens read as one flow.

import { useRef, useState, type FormEvent } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link as MuiLink,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { tokens } from "@findgeeks/ui-tokens";
import { ENV } from "@/lib/env";
import {
  CaptchaWidget,
  type CaptchaWidgetHandle,
} from "@/components/CaptchaWidget";

interface NavState {
  identifier?: string;
}

export function ResetPasswordWithCode() {
  const navigate = useNavigate();
  const location = useLocation();
  const navState = (location.state ?? null) as NavState | null;
  const [identifier, setIdentifier] = useState(navState?.identifier ?? "");
  const [otpCode, setOtpCode] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const captchaRef = useRef<CaptchaWidgetHandle | null>(null);

  const passwordsMatch = pw.length > 0 && pw === pw2;
  const passwordLongEnough = pw.length >= 8;
  const codeLooksOk = /^\d{6}$/.test(otpCode);
  const canSubmit =
    identifier.trim().length > 0 &&
    codeLooksOk &&
    passwordsMatch &&
    passwordLongEnough &&
    captchaToken !== null &&
    !submitting;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${ENV.API_BASE_URL}/v1/auth/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Captcha-Token": captchaToken!,
        },
        body: JSON.stringify({
          identifier: identifier.trim(),
          otp_code: otpCode,
          new_password: pw,
        }),
      });
      captchaRef.current?.reset();
      setCaptchaToken(null);

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const code = body?.error ?? "";
        let message =
          body?.message ?? body?.error ?? "Reset failed. Try again.";
        if (code === "otp_invalid" || code === "reset_code_invalid") {
          message =
            "That code is incorrect or has expired. Request a new one from the previous page.";
        } else if (code === "admin_recovery_locked") {
          message =
            "Too many recovery attempts on this admin account. Try again in a few minutes.";
        } else if (code === "too_many_requests") {
          message =
            "Too many recovery attempts from this network. Try again in a few minutes.";
        }
        setError(message);
        return;
      }
      navigate("/login", {
        replace: true,
        state: { toast: "Password reset. Please sign in." },
      });
    } catch (err) {
      setError((err as Error).message ?? "Network error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: tokens.colors.admin.signInBg,
        backgroundImage: "url(/login-bg.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Paper elevation={6} sx={{ width: "100%", maxWidth: 450, p: 4 }}>
        <Typography
          component="h1"
          sx={{
            fontSize: tokens.font.sizes.h1,
            fontWeight: tokens.font.weights.semibold,
            mb: 1,
            color: tokens.colors.admin.textHeading,
          }}
        >
          Enter reset code
        </Typography>
        <Typography
          variant="body2"
          sx={{ mb: 3, color: tokens.colors.admin.textBody }}
        >
          We sent a 6-digit code to the email on file for your account. Enter
          it below along with a new password.
        </Typography>

        <Box component="form" onSubmit={onSubmit} noValidate>
          <TextField
            label="Username, email, or phone"
            fullWidth
            required
            autoComplete="username"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="6-digit code"
            fullWidth
            required
            inputProps={{
              inputMode: "numeric",
              pattern: "\\d{6}",
              maxLength: 6,
              autoComplete: "one-time-code",
            }}
            value={otpCode}
            onChange={(e) =>
              setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            error={otpCode.length > 0 && !codeLooksOk}
            helperText={
              otpCode.length > 0 && !codeLooksOk
                ? "Code is 6 digits."
                : undefined
            }
            sx={{ mb: 2 }}
          />
          <TextField
            label="New password"
            type="password"
            fullWidth
            required
            autoComplete="new-password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            error={pw.length > 0 && !passwordLongEnough}
            helperText={
              pw.length > 0 && !passwordLongEnough
                ? "At least 8 characters."
                : undefined
            }
            sx={{ mb: 2 }}
          />
          <TextField
            label="Confirm new password"
            type="password"
            fullWidth
            required
            autoComplete="new-password"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            error={pw2.length > 0 && !passwordsMatch}
            helperText={
              pw2.length > 0 && !passwordsMatch
                ? "Passwords do not match."
                : undefined
            }
            sx={{ mb: 3 }}
          />

          <Box sx={{ mb: 2 }}>
            <CaptchaWidget
              ref={captchaRef}
              onToken={(t) => setCaptchaToken(t)}
              onError={() =>
                setError("Unable to verify captcha. Please try again.")
              }
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} role="alert">
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={!canSubmit}
            sx={{
              py: 1.25,
              borderRadius: 9999,
              background: tokens.colors.admin.primary,
              "&:hover": { background: tokens.colors.admin.primary },
            }}
          >
            {submitting ? <CircularProgress size={20} /> : "Set new password"}
          </Button>
        </Box>

        <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between" }}>
          <MuiLink
            component={RouterLink}
            to="/forgot-password"
            underline="hover"
            variant="body2"
          >
            Request a new code
          </MuiLink>
          <MuiLink
            component={RouterLink}
            to="/login"
            underline="hover"
            variant="body2"
          >
            Back to Sign In
          </MuiLink>
        </Box>
      </Paper>
    </Box>
  );
}
